import React, { useRef, useState, useEffect, useImperativeHandle, useCallback } from 'react';
import TextField from '@material-ui/core/TextField';

// Custom contentEditable input component passed to Material-UI TextField via InputProps.inputComponent
const WysiwygContentInput = React.forwardRef((props, ref) => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    className,
    style,
    disabled,
    readOnly,
    rows,
    autoFocus,
    ...other
  } = props;

  const contentRef = useRef(null);
  const isTypingRef = useRef(false);

  // Helper to check if HTML content is visually empty
  const checkIsEmpty = (html) => {
    if (!html) return true;
    const clean = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
    return clean.length === 0;
  };

  const [isEmpty, setIsEmpty] = useState(checkIsEmpty(value));

  // Expose focus and value to Material-UI InputBase
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (contentRef.current) {
        contentRef.current.focus();
      }
    },
    value: value || '',
    scrollWidth: contentRef.current ? contentRef.current.scrollWidth : 0,
    scrollHeight: contentRef.current ? contentRef.current.scrollHeight : 0,
  }));

  // Helper to get total character count from start of editor to current caret
  const getCaretCharacterOffset = (element) => {
    let caretOffset = 0;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      try {
        const range = sel.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        caretOffset = preCaretRange.toString().length;
      } catch (e) {
        caretOffset = 0;
      }
    }
    return caretOffset;
  };

  // Helper to create a DOM range between two character offsets inside an element
  const createRangeAtOffset = (element, startOffset, endOffset) => {
    let charIndex = 0;
    const range = document.createRange();
    range.setStart(element, 0);
    range.collapse(true);

    let foundStart = false;
    let stop = false;

    const traverse = (node) => {
      if (stop) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const nextCharIndex = charIndex + node.length;
        if (!foundStart && startOffset >= charIndex && startOffset <= nextCharIndex) {
          range.setStart(node, Math.max(0, startOffset - charIndex));
          foundStart = true;
        }
        if (foundStart && endOffset >= charIndex && endOffset <= nextCharIndex) {
          range.setEnd(node, Math.max(0, endOffset - charIndex));
          stop = true;
          return;
        }
        charIndex = nextCharIndex;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          traverse(node.childNodes[i]);
          if (stop) break;
        }
      }
    };

    try {
      traverse(element);
      if (!foundStart) {
        range.selectNodeContents(element);
        range.collapse(false);
      } else if (!stop) {
        range.setEnd(element, element.childNodes.length);
      }
    } catch (e) {
      range.selectNodeContents(element);
      range.collapse(false);
    }
    return range;
  };

  // Sync external value with editor content when not actively typing
  useEffect(() => {
    if (contentRef.current && !isTypingRef.current) {
      const currentHtml = contentRef.current.innerHTML;
      const targetVal = value || '';
      if (currentHtml !== targetVal) {
        contentRef.current.innerHTML = targetVal;
        setIsEmpty(checkIsEmpty(targetVal));
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!contentRef.current) return;
    isTypingRef.current = true;
    const html = contentRef.current.innerHTML;
    const textEmpty = checkIsEmpty(html);
    setIsEmpty(textEmpty);

    if (onChange) {
      onChange({
        target: {
          value: textEmpty ? '' : html,
          name: props.name || 'description',
        },
      });
    }

    setTimeout(() => {
      isTypingRef.current = false;
    }, 0);
  }, [onChange, props.name]);

  const handleKeyDown = (e) => {
    // Rich Text Formatting Shortcuts: Ctrl+B, Ctrl+I, Ctrl+U
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === 'b') {
        e.preventDefault();
        document.execCommand('bold', false, null);
        handleInput();
        return;
      }
      if (key === 'i') {
        e.preventDefault();
        document.execCommand('italic', false, null);
        handleInput();
        return;
      }
      if (key === 'u') {
        e.preventDefault();
        document.execCommand('underline', false, null);
        handleInput();
        return;
      }
    }

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !contentRef.current) return;
    const range = sel.getRangeAt(0);

    // 1. Space key: convert "- " or "* " to bullet
    if (e.key === ' ' || e.code === 'Space') {
      if (range.collapsed) {
        let handled = false;

        // Fast-path: Check current text node
        if (range.startContainer.nodeType === Node.TEXT_NODE) {
          const textBefore = range.startContainer.textContent.slice(0, range.startOffset);
          const match = textBefore.match(/(?:^|\n)(\s*)([-*])$/);
          if (match) {
            e.preventDefault();
            const len = match[1].length + match[2].length;
            const r = document.createRange();
            r.setStart(range.startContainer, range.startOffset - len);
            r.setEnd(range.startContainer, range.startOffset);
            sel.removeAllRanges();
            sel.addRange(r);

            const indent = match[1].length >= 4 ? match[1] : '    ';
            let bulletChar = '•';
            if (indent.length >= 12) bulletChar = '▪';
            else if (indent.length >= 8) bulletChar = '◦';
            document.execCommand('insertText', false, `${indent}${bulletChar} `);
            handleInput();
            handled = true;
          }
        }

        // Fallback: Check text before caret in element
        if (!handled) {
          const pos = getCaretCharacterOffset(contentRef.current);
          const fullText = contentRef.current.innerText || '';
          const textBefore = fullText.slice(0, pos);
          const lineStart = textBefore.lastIndexOf('\n') + 1;
          const currentLine = textBefore.slice(lineStart);
          const match = currentLine.match(/^(\s*)([-*])$/);
          if (match) {
            e.preventDefault();
            const r = createRangeAtOffset(contentRef.current, lineStart, pos);
            sel.removeAllRanges();
            sel.addRange(r);

            const indent = match[1].length >= 4 ? match[1] : '    ';
            let bulletChar = '•';
            if (indent.length >= 12) bulletChar = '▪';
            else if (indent.length >= 8) bulletChar = '◦';
            document.execCommand('insertText', false, `${indent}${bulletChar} `);
            handleInput();
          }
        }
      }
      return;
    }

    // 2. Tab key: convert "- + Tab" to bullet OR indent/outdent existing bullet
    if (e.key === 'Tab') {
      e.preventDefault();

      // Check if user typed "-" or "*" right before cursor
      let isDashTrigger = false;
      if (range.collapsed && range.startContainer.nodeType === Node.TEXT_NODE) {
        const textBefore = range.startContainer.textContent.slice(0, range.startOffset);
        const match = textBefore.match(/(?:^|\n)(\s*)([-*])$/);
        if (match && !e.shiftKey) {
          const len = match[1].length + match[2].length;
          const r = document.createRange();
          r.setStart(range.startContainer, range.startOffset - len);
          r.setEnd(range.startContainer, range.startOffset);
          sel.removeAllRanges();
          sel.addRange(r);

          const indent = match[1].length >= 4 ? match[1] : '    ';
          let bulletChar = '•';
          if (indent.length >= 12) bulletChar = '▪';
          else if (indent.length >= 8) bulletChar = '◦';
          document.execCommand('insertText', false, `${indent}${bulletChar} `);
          handleInput();
          return;
        }
      }

      const pos = getCaretCharacterOffset(contentRef.current);
      const fullText = contentRef.current.innerText || '';
      const textBefore = fullText.slice(0, pos);
      const lineStart = textBefore.lastIndexOf('\n') + 1;
      const currentLineBeforeCursor = textBefore.slice(lineStart);
      const dashMatch = currentLineBeforeCursor.match(/^(\s*)([-*])$/);

      if (dashMatch && !e.shiftKey) {
        const r = createRangeAtOffset(contentRef.current, lineStart, pos);
        sel.removeAllRanges();
        sel.addRange(r);

        const indent = dashMatch[1].length >= 4 ? dashMatch[1] : '    ';
        let bulletChar = '•';
        if (indent.length >= 12) bulletChar = '▪';
        else if (indent.length >= 8) bulletChar = '◦';
        document.execCommand('insertText', false, `${indent}${bulletChar} `);
        handleInput();
        return;
      }

      // Otherwise, inspect current line for existing bullet to indent/outdent
      const lineEndRel = fullText.slice(pos).indexOf('\n');
      const lineEnd = lineEndRel === -1 ? fullText.length : pos + lineEndRel;
      const currentLine = fullText.slice(lineStart, lineEnd);
      const bulletMatch = currentLine.match(/^(\s*)([•◦▪])\s*(.*)$/);

      if (bulletMatch) {
        const bulletChar = bulletMatch[2];
        const oldPrefix = bulletMatch[1] + bulletChar + ' ';
        let newPrefix = '';

        if (e.shiftKey) {
          // Outdent
          if (bulletChar === '▪') {
            newPrefix = '        ◦ ';
          } else if (bulletChar === '◦') {
            newPrefix = '    • ';
          } else {
            // Outdent from level 1 removes bullet
            newPrefix = '';
          }
        } else {
          // Indent
          if (bulletChar === '•') {
            newPrefix = '        ◦ ';
          } else if (bulletChar === '◦') {
            newPrefix = '            ▪ ';
          } else {
            newPrefix = '            ▪ ';
          }
        }

        const replaceRange = createRangeAtOffset(contentRef.current, lineStart, lineStart + oldPrefix.length);
        sel.removeAllRanges();
        sel.addRange(replaceRange);
        document.execCommand('insertText', false, newPrefix);

        const newPos = Math.max(
          lineStart + newPrefix.length,
          pos + (newPrefix.length - oldPrefix.length)
        );
        const caretRange = createRangeAtOffset(contentRef.current, newPos, newPos);
        sel.removeAllRanges();
        sel.addRange(caretRange);

        handleInput();
        return;
      }

      // If not on bullet line and Tab pressed: insert 4 spaces
      if (!e.shiftKey) {
        document.execCommand('insertText', false, '    ');
        handleInput();
        return;
      }
    }

    // 3. Enter key handling: continue bullet or exit empty bullet
    if (e.key === 'Enter') {
      const pos = getCaretCharacterOffset(contentRef.current);
      const fullText = contentRef.current.innerText || '';
      const textBefore = fullText.slice(0, pos);
      const lineStart = textBefore.lastIndexOf('\n') + 1;
      const lineEndRel = fullText.slice(pos).indexOf('\n');
      const lineEnd = lineEndRel === -1 ? fullText.length : pos + lineEndRel;
      const currentLine = fullText.slice(lineStart, lineEnd);

      const bulletMatch = currentLine.match(/^(\s*)([•◦▪])\s*(.*)$/);

      if (bulletMatch) {
        e.preventDefault();
        const hasContent = Boolean(bulletMatch[3].trim());

        if (hasContent) {
          // Continue bullet on new line
          const prefix = bulletMatch[1] + bulletMatch[2] + ' ';
          document.execCommand('insertText', false, '\n' + prefix);
          handleInput();
          return;
        } else {
          // Empty bullet line: Outdent or exit list
          const bulletChar = bulletMatch[2];
          const oldPrefix = bulletMatch[1] + bulletChar + ' ';
          let newPrefix = '';

          if (bulletChar === '▪') {
            newPrefix = '        ◦ ';
          } else if (bulletChar === '◦') {
            newPrefix = '    • ';
          } else {
            newPrefix = '';
          }

          const replaceRange = createRangeAtOffset(contentRef.current, lineStart, lineStart + oldPrefix.length);
          sel.removeAllRanges();
          sel.addRange(replaceRange);
          document.execCommand('insertText', false, newPrefix);

          const caretRange = createRangeAtOffset(contentRef.current, lineStart + newPrefix.length, lineStart + newPrefix.length);
          sel.removeAllRanges();
          sel.addRange(caretRange);

          handleInput();
          return;
        }
      }
    }

    // 4. Backspace on empty bullet: clear the bullet immediately
    if (e.key === 'Backspace' && range.collapsed) {
      if (range.startContainer.nodeType === Node.TEXT_NODE) {
        const textBefore = range.startContainer.textContent.slice(0, range.startOffset);
        const match = textBefore.match(/(?:^|\n)(\s*)([•◦▪])\s?$/);
        if (match) {
          e.preventDefault();
          const len = match[1].length + match[2].length + (match[0].endsWith(' ') ? 1 : 0);
          const r = document.createRange();
          r.setStart(range.startContainer, range.startOffset - len);
          r.setEnd(range.startContainer, range.startOffset);
          sel.removeAllRanges();
          sel.addRange(r);
          document.execCommand('delete');
          handleInput();
          return;
        }
      }
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div
      ref={contentRef}
      id={props.id}
      className={`tm-wysiwyg-content ${className || ''}`}
      contentEditable={!disabled && !readOnly}
      suppressContentEditableWarning
      data-empty={isEmpty}
      data-placeholder={placeholder || ''}
      onFocus={(e) => {
        if (onFocus) onFocus(e);
      }}
      onBlur={(e) => {
        handleInput();
        if (onBlur) onBlur(e);
      }}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      style={{
        position: 'relative',
        minHeight: 110,
        maxHeight: 400,
        outline: 'none',
        width: '100%',
        textAlign: 'left',
        direction: 'ltr',
        cursor: 'text',
        ...style,
      }}
      {...other}
    />
  );
});

// Full Material-UI TextField with WysiwygContentInput as its inputComponent
const WysiwygDescriptionEditor = ({
  value = '',
  onChange,
  onBlur,
  label = 'Description',
  placeholder = 'Enter description...',
  error = false,
  helperText = '',
  id = 'description',
  name = 'description',
  rows = 4,
  className = '',
  ...otherProps
}) => {
  return (
    <TextField
      fullWidth
      multiline
      rows={rows}
      variant="outlined"
      id={id}
      name={name}
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onChange={(e) => {
        if (onChange) {
          onChange(e.target.value);
        }
      }}
      onBlur={onBlur}
      error={Boolean(error)}
      helperText={helperText}
      className={`tm-wysiwyg-mui-field ${error ? 'input-error' : ''} ${className}`}
      InputProps={{
        inputComponent: WysiwygContentInput,
      }}
      inputProps={{
        placeholder: placeholder,
        id: id,
        name: name,
      }}
      {...otherProps}
    />
  );
};

export default WysiwygDescriptionEditor;
